/**
 * The dashboard: the first screen after sign-in — a scannable overview, not a
 * stack of full sections. A KPI strip, then a bento of calm widgets: what
 * needs you (a count and the one most urgent item, the rest one click away),
 * Auto-apply status, the pipeline at a glance, and the newest matches.
 * Everything here is a read; acting happens on the screen each card links to.
 */

import { useEffect, useState } from "react";
import { api, ApiError } from "./auth";
import { AutoRun } from "./AutoRun";
import { browserFrom } from "./fillHint";
import type { ExtensionStatus } from "./extension";

/** This browser, once: "chromium" runs the extension; "other" (Safari, Firefox) does not. */
const BROWSER = browserFrom(
  navigator.userAgent,
  (navigator as unknown as { userAgentData?: { brands?: ReadonlyArray<{ brand: string }> } }).userAgentData?.brands ?? null,
);
const BROWSER_NOTICE_KEY = "jobcork.browserNotice.dismissed";

type Stage = "saved" | "applied" | "screening" | "interviewing" | "offer" | "rejected" | "closed";

interface DashboardMatch {
  jobId: string;
  title: string;
  employer: string;
  place: string | null;
  isRemote: boolean;
  comp: { minCents: number | null; maxCents: number | null } | null;
  applyUrl: string;
  isNew: boolean;
}
interface NeedsYouItem { jobId: string; title: string; employer: string; applyUrl: string | null; reason: string; humanCheck: boolean; questionsLeft?: number; }
interface FollowUpDue { jobId: string; title: string; employer: string; stage: string; followUpOn: string; notes: string | null; }
/** The fast half of the dashboard (/api/dashboard): scoped reads that land first. */
interface DashboardCore {
  followUpsDue: FollowUpDue[];
  needsYou: NeedsYouItem[];
  sentThisWeek: number;
  /** Applications actually submitted, all-time — the honest "sent" for the auto-apply pipeline. */
  sentTotal: number;
  /** Applications waiting on the person, all-time — the full count behind the needsYou preview. */
  needsYouTotal: number;
}
/** The slow half (/api/dashboard/ranked): the full ranking, fetched separately. */
interface DashboardRanked {
  totalMatches: number;
  newMatches: number;
  topMatches: DashboardMatch[];
  /** How the matches split across the three send routes — the clickable breakdown. */
  routeCounts: { autopilot: number; copilot: number; manual: number };
}

interface DashboardProps {
  me: {
    pipeline: Record<Stage, number>;
    budget: { dailyRemaining: number; monthlyRemaining: number };
    /** Auto at a glance (E14). */
    auto?: { mode: "auto" | "review"; cloud: boolean; sentToday: number; needsYou: number; browsers: number };
    /** The person's tracks, for naming the active one and tagging cross-track needs-you rows. */
    tracks?: Array<{ name: string; active: boolean }>;
    /** Whether the active track has a search of its own — a fresh track has none. */
    onboarding?: { preferencesSet: boolean };
  };
  /** The extension on this page, or null while still asking (or absent in a non-Chrome browser). */
  ext: ExtensionStatus | null;
  onView: (v: "matches" | "applications" | "preferences" | "auto-apply", route?: "autopilot" | "copilot" | "manual") => void;
  onTailor: (jobId: string) => void;
  /** Open the Applications page scrolled to this one application, lit up. */
  onReview: (jobId: string) => void;
  /** Open one application's open questions, and nothing else (AnswerLeft). */
  onAnswerLeft?: (jobId: string) => void;
}

/**
 * A heads-up when this browser can't run the finishing steps. "Finish in Chrome"
 * and JobCork's auto-fill need Chrome with the extension; opened in Safari they
 * just land on the employer's bare form. Better to say so before a click than to
 * let it fail silently. Dismissible, and it remembers the dismissal.
 */
function BrowserNotice({ ext }: { ext: ExtensionStatus | null }) {
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(BROWSER_NOTICE_KEY) === "1"; } catch { return false; }
  });
  if (dismissed) return null;

  let text: string | null = null;
  let connect = false;
  if (BROWSER === "other") {
    text = "You're not in Chrome. Copilot and “Finish in Chrome” run in Chrome with the JobCork extension — open jobcork.com in Chrome to use them. You can still use “Send with JobCork” on any match to send from the cloud after you approve what goes.";
  } else if (ext !== null && !ext.installed) {
    text = "The JobCork extension isn't installed in this browser, so finishing an application opens the employer's plain form. Add the extension to fill and finish here, or use “Send with JobCork” to send from the cloud.";
    connect = true;
  }
  if (!text) return null;

  const dismiss = () => {
    try { localStorage.setItem(BROWSER_NOTICE_KEY, "1"); } catch { /* fine */ }
    setDismissed(true);
  };
  return (
    <div className="browser-notice" role="note">
      <span>{text}{connect && <> <a href="#extension">Connect the extension →</a></>}</span>
      <button className="notice-x" type="button" aria-label="Dismiss" onClick={dismiss}>×</button>
    </div>
  );
}

function describe(err: unknown): string {
  if (err instanceof ApiError) return err.messages.join(" ");
  return err instanceof Error ? err.message : String(err);
}

function money(cents: number | null): string | null {
  return cents === null ? null : `$${Math.round(cents / 100).toLocaleString("en-US")}`;
}
function payLine(comp: DashboardMatch["comp"]): string | null {
  if (!comp) return null;
  const lo = money(comp.minCents), hi = money(comp.maxCents);
  if (lo && hi) return `${lo}–${hi}`;
  if (lo) return `from ${lo}`;
  if (hi) return `up to ${hi}`;
  return null;
}
function whereLine(m: DashboardMatch): string {
  return [m.employer, m.isRemote ? "Remote" : m.place, payLine(m.comp)].filter(Boolean).join(" · ");
}
function dueWord(on: string, today: string): string {
  const d = Math.round((Date.parse(`${today}T00:00:00Z`) - Date.parse(`${on}T00:00:00Z`)) / 86_400_000);
  return d <= 0 ? "today" : d === 1 ? "yesterday" : `${d} days ago`;
}

export function Dashboard({ me, ext, onView, onTailor, onReview, onAnswerLeft }: DashboardProps) {
  const [core, setCore] = useState<DashboardCore | null>(null);
  const [ranked, setRanked] = useState<DashboardRanked | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [rankedError, setRankedError] = useState<string | null>(null);

  const needsSearch = me.onboarding ? !me.onboarding.preferencesSet : false;

  // The fast, scoped reads (needs-you, follow-ups, this week's sends) land first.
  useEffect(() => {
    api<DashboardCore>("/api/dashboard").then(setCore).catch((e) => setError(describe(e)));
  }, []);

  // The full ranking is its own request -- several seconds -- so the ranking
  // cards below show a loading state until it lands instead of holding up the
  // whole screen. A track with no search of its own has nothing to rank.
  useEffect(() => {
    if (needsSearch) return;
    api<DashboardRanked>("/api/dashboard/ranked").then(setRanked).catch((e) => setRankedError(describe(e)));
  }, [needsSearch]);

  const p = me.pipeline;
  const inPlay = p.applied + p.screening + p.interviewing + p.offer;
  const applied = inPlay + p.rejected + p.closed;
  const interviews = p.screening + p.interviewing + p.offer;
  const today = new Date().toISOString().slice(0, 10);
  // "—" until the half that carries this number has landed: core for the sent
  // count, ranked for the match totals.
  const figCore = (n: number | undefined) => (core ? String(n ?? 0) : "—");
  const figRanked = (n: number | undefined) => (ranked ? String(n ?? 0) : "—");
  const tracks = me.tracks ?? [];
  const multiTrack = tracks.length > 1;
  const activeTrack = tracks.find((t) => t.active)?.name ?? null;

  // Not here yet, and no error -- distinct from arrived-and-empty. Without this
  // a card shows its EMPTY-state copy the whole time it is loading ("Nothing
  // needs you", "Ranked matches appear here"), which reads as "your account is
  // empty" until the call lands. Show a loading line instead, per half: the
  // needs-you card waits on core, the ranking cards on the slower ranked call.
  const coreLoading = !core && !error;
  const rankedLoading = !ranked && !rankedError;

  // Needs you: the count, and the single most urgent item; the rest is one click away.
  const needs = core?.needsYou ?? [];
  const followUps = core?.followUpsDue ?? [];
  const needCount = needs.length + followUps.length;
  const topNeed = needs[0] ?? null;
  const topFollow = !topNeed ? (followUps[0] ?? null) : null;

  const auto = me.auto;
  const runsSomewhere = !!auto && (auto.browsers > 0 || auto.cloud);
  const pipeMax = Math.max(1, p.saved, applied, inPlay, interviews);
  const bar = (n: number) => `${Math.round((n / pipeMax) * 100)}%`;

  return (
    <>
      <BrowserNotice ext={ext} />

      <section className="dash-glance">
        <div className="dash-stats">
          <button className="stat stat-tap" onClick={() => onView(needsSearch ? "preferences" : "matches")}>
            <span className="fig">{needsSearch ? "—" : figRanked(ranked?.totalMatches)}</span>
            <span className="cap">{needsSearch ? "no search yet" : `matches${ranked && ranked.newMatches > 0 ? ` · ${ranked.newMatches} new` : ""}`}</span>
          </button>
          <button className="stat stat-tap" onClick={() => onView("applications")}>
            <span className="fig">{applied}</span>
            <span className="cap">applied</span>
          </button>
          <button className="stat stat-tap" onClick={() => onView("applications")}>
            <span className="fig">{inPlay}</span>
            <span className="cap">in play</span>
          </button>
          <div className="stat">
            <span className="fig">{figCore(core?.sentThisWeek)}</span>
            <span className="cap">sent this week</span>
          </div>
        </div>
        {error && <p className="err small">{error}</p>}
      </section>

      {!needsSearch && ranked && (ranked.routeCounts.autopilot + ranked.routeCounts.copilot + ranked.routeCounts.manual) > 0 && (
        <section className="route-section" aria-label="Your matches by how they'd be applied">
          <p className="route-head muted small">
            Your {ranked.totalMatches} matches, by how they'd be applied — the kind of application each is, not a count of applications that will go through.
          </p>
          <div className="route-breakdown">
            <button className="rb rb-auto" onClick={() => onView("matches", "autopilot")} title="JobCork sends these from the cloud, your computer off. Click to see them.">
              <span className="rb-n">{ranked.routeCounts.autopilot}</span>
              <span className="rb-l">Autopilot</span>
              <span className="rb-s">cloud can send</span>
            </button>
            <button className="rb rb-copilot" onClick={() => onView("matches", "copilot")} title="These go out from your own browser, which clears the robot check the cloud gets. Click to see them.">
              <span className="rb-n">{ranked.routeCounts.copilot}</span>
              <span className="rb-l">Copilot</span>
              <span className="rb-s">needs your browser</span>
            </button>
            <button className="rb rb-manual" onClick={() => onView("matches", "manual")} title="JobCork can't fill these — you apply on the employer's site. Click to see them.">
              <span className="rb-n">{ranked.routeCounts.manual}</span>
              <span className="rb-l">Manual</span>
              <span className="rb-s">you apply</span>
            </button>
          </div>
          {core && (ranked.routeCounts.autopilot + ranked.routeCounts.copilot) > 0 && (
            <p className="route-pipeline small">
              Auto-apply so far: <strong>{core.sentTotal}</strong> sent · <strong>{core.needsYouTotal}</strong> need you, of the {ranked.routeCounts.autopilot + ranked.routeCounts.copilot} JobCork can try for you. It only knows it can finish an application once it opens the form, so that number is what it can attempt — not a promise. It works through them while your browser is open.
            </p>
          )}
        </section>
      )}

      {needsSearch ? (
        <section className="card">
          <h2 style={{ marginTop: 0 }}>Set up your search{multiTrack && activeTrack ? ` — ${activeTrack}` : ""}</h2>
          <p className="muted small">
            {multiTrack && activeTrack ? `Your ${activeTrack} track doesn't have a search yet. ` : "This track doesn't have a search yet. "}
            Set what you're looking for — titles, places, a pay floor — and its matches appear here.
          </p>
          <div className="row"><button className="btn" onClick={() => onView("preferences")}>Set what you're looking for</button></div>
        </section>
      ) : (
        <div className="dash-bento">
          {/* Needs you — calm: a count and the one most urgent, the rest one click away */}
          <section className={`dw${needCount > 0 ? " dw-attention" : ""}`}>
            <div className="dw-head">
              <h2>Needs you</h2>
              {needCount > 0 && <span className="count-chip">{needCount}</span>}
            </div>
            {coreLoading ? (
              <p className="muted small nomargin">Loading…</p>
            ) : needCount === 0 ? (
              <p className="muted small nomargin">Nothing needs you right now.</p>
            ) : topNeed ? (
              <>
                <div className="dw-item">
                  <div className="mp-main">
                    <strong>{topNeed.title}</strong> <span className="muted">— {topNeed.employer}</span>
                    <p className="muted small nomargin">{topNeed.reason}</p>
                  </div>
                  {onAnswerLeft && (topNeed.questionsLeft ?? 0) > 0
                    ? <button className="btn small" onClick={() => onAnswerLeft(topNeed.jobId)}>Answer what's left</button>
                    : <button className="btn ghost small" onClick={() => onReview(topNeed.jobId)}>Review</button>}
                </div>
                {needCount > 1 && <button className="dw-more" onClick={() => onView("applications")}>{needCount - 1} more need you →</button>}
              </>
            ) : topFollow ? (
              <>
                <div className="dw-item">
                  <div className="mp-main">
                    <strong>{topFollow.title}</strong> <span className="muted">— {topFollow.employer}, follow-up due {dueWord(topFollow.followUpOn, today)}</span>
                  </div>
                  <button className="btn ghost small" onClick={() => onReview(topFollow.jobId)}>Review</button>
                </div>
                {needCount > 1 && <button className="dw-more" onClick={() => onView("applications")}>{needCount - 1} more →</button>}
              </>
            ) : null}
          </section>

          {/* Auto-apply status */}
          <section className="dw">
            <div className="dw-head">
              <h2>Auto-apply</h2>
              <span className="status-line">
                <span className={`auto-dot${auto?.mode === "auto" && runsSomewhere ? " on" : ""}`} />
                <span className="muted small">{auto?.mode === "auto" ? (runsSomewhere ? "On" : "On · idle") : "Off"}</span>
              </span>
            </div>
            {auto?.mode === "auto" ? (
              <>
                <div className="dw-big">{auto.sentToday}<span className="unit">sent today</span></div>
                <p className="muted small nomargin">
                  {runsSomewhere
                    ? `Running ${auto.browsers > 0 && auto.cloud ? "in the cloud and your browser" : auto.browsers > 0 ? "in your browser" : "in the cloud"}.`
                    : "On, but nothing is set to run it — switch on the cloud or connect a browser."}
                  {auto.needsYou > 0 ? ` ${auto.needsYou} need you.` : ""}
                </p>
                <div className="dw-foot">
                  <AutoRun mode="auto" onReview={onReview} />
                  <button className="dw-more" onClick={() => onView("auto-apply")}>Auto's rules →</button>
                </div>
              </>
            ) : (
              <>
                <p className="muted small">JobCork can find your strong matches, fill them and send them — by your rules, with a full record. Anything it can't answer comes back to you.</p>
                <div className="row" style={{ marginTop: "auto" }}><button className="btn small" onClick={() => onView("auto-apply")}>Turn on Auto-apply</button></div>
              </>
            )}
          </section>

          {/* Pipeline mini funnel */}
          <section className="dw">
            <div className="dw-head"><h2>Pipeline</h2><button className="dw-more" onClick={() => onView("applications")}>Applications →</button></div>
            <div className="pipe">
              {([["saved", p.saved], ["applied", applied], ["in play", inPlay], ["interview", interviews]] as Array<[string, number]>).map(([label, n]) => (
                <div className="pipe-st" key={label}>
                  <div className="pipe-n">{n}</div>
                  <div className="pipe-l">{label}</div>
                  <div className="pipe-track"><i style={{ width: bar(n) }} /></div>
                </div>
              ))}
            </div>
          </section>

          {/* Top matches, compact */}
          <section className="dw">
            <div className="dw-head">
              <h2>Top matches{multiTrack && activeTrack ? <span className="track-chip">{activeTrack}</span> : null}</h2>
              <button className="dw-more" onClick={() => onView("matches")}>See all →</button>
            </div>
            {rankedLoading ? (
              <p className="muted small nomargin">Loading…</p>
            ) : rankedError ? (
              <p className="err small nomargin">{rankedError}</p>
            ) : ranked && ranked.topMatches.length > 0 ? (
              <div className="mini">
                {ranked.topMatches.slice(0, 3).map((m) => (
                  <div className="mini-row" key={m.jobId}>
                    <div className="mp-main">
                      <div className="mp-titlerow">
                        <a href={m.applyUrl} target="_blank" rel="noreferrer noopener" className="mp-title">{m.title}</a>
                        {m.isNew && <span className="tag new">new</span>}
                      </div>
                      <div className="muted small">{whereLine(m)}</div>
                    </div>
                    <button className="btn ghost small" onClick={() => onTailor(m.jobId)}>Tailor</button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="muted small nomargin">Ranked matches appear here.</p>
            )}
          </section>
        </div>
      )}
    </>
  );
}
